import { defineComponent as _defineComponent } from 'vue'
import { AxiosError, AxiosResponse } from 'axios';
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router/composables';

import canRequestDonationReceipt from '@/composables/canRequestDonationReceipt';
import isPostalAddressRequired from '@/composables/isPostalAddressRequired';
import isRechargeRequired from '@/composables/isRechargeRequired';
import api from '~api/api';
import redeem from '~api/redeem';
import store from '~store';
import LoadingIndicator from '~widget/LoadingIndicator.vue';

import availabilityWarning from './AvailabilityWarning.vue';
import payment from './Payment.vue';
import rechargeForm from './RechargeForm.vue';
import redeemForm from './RedeemForm.vue';
import redeemWarnings from './RedeemWarnings.vue';
import shopInfo from './ShopInfo.vue';
import shopPromotion from './ShopPromotion.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'ShopDetails',
  props: {
  /* ID of the Shop like '5' or '1189' */
  id: {
    type: String,
    default: null,
  },
  /* URL-Slug of shop like '/example-shop' */
  slug: {
    type: String,
    default: null,
  },
},
  setup(__props) {

const props = __props

const form = store.state.customer?.form;

const route = useRoute();
const router = useRouter();



const step = ref(0);
const wizardComponents = ref([
  {
    name: 'shopInfo',
    component: shopInfo,
  },
  {
    name: 'availabilityWarning',
    component: availabilityWarning,
    props: {
      alternativeShops: null,
    },
  },
  {
    name: 'shopPromotion',
    component: shopPromotion,
  },
  {
    name: 'redeemWarnings',
    component: redeemWarnings,
  },
  {
    name: 'payment',
    component: payment,
  },
  {
    name: 'redeemForm',
    component: redeemForm,
  },
  {
    name: 'rechargeForm',
    component: rechargeForm,
  },
]);
const submitted = ref(false);
const errors = ref([]);

const shop = computed(() => store.getters.getShopDetails);
const country = computed(() => store.getters.getCountry);
const voucherToken = computed(() => store.getters.getVoucherToken);
const redeemCampaign = computed(() => store.getters.getRedeemCampaign);
const voucher = computed(() => store.getters.getVoucher);

const voucherSerialNumber = ref('');

function setFormData(
  data: unknown,
): void {
  Object.assign(form, data);
  sessionStorage.setItem('formData', JSON.stringify(form));
}

function clearFormData(): void {
  sessionStorage.removeItem('formData');
}

function switchComponent(
  componentName: string,
): void {
  const idx = wizardComponents.value.findIndex((c) => c.name === componentName);
  if (idx !== -1) {
    if (sessionStorage.getItem('formData')) {
      setFormData(JSON.parse(sessionStorage.getItem('formData') || ''));
    }
    step.value = idx;
  }
}

function prev(
  data: unknown,
): void {
  setFormData(data);

  switch (wizardComponents.value[step.value].name) {
    case 'payment':
    case 'redeemWarnings':
      step.value = wizardComponents.value.findIndex((c) => c.name === 'shopInfo');
      break;

    default:
      step.value -= 1;
      break;
  }
}

function handleSubmitForm(
  responseData: Record<string, string>,
): void {
  sessionStorage.setItem('voucherBalanceAfterRedeem', responseData.remainingBalance);
  store.dispatch('removeVoucher');
  store.dispatch('setRedeemIdForGiveaway', responseData?.redeemId);
  router.push({ name: 'thankYou', query: { shop: props.slug, voucherSerialNumber: voucherSerialNumber.value } });
}

function submitForm(
  callback: (param: Record<string, string>) => void,
): void {
  if (!submitted.value) {
    submitted.value = true;

    const customer = {
      salutation: form.salutation,
      firstName: form.firstName,
      lastName: form.lastName,
      email: form.email,
      email2: form.email2,
      phone: form.phone,
      country: country.value,
      useDataConfirmation: true,
      newShopCustomer: form?.newShopCustomer ?? null,
    } as Record<string, string>;

    if (isPostalAddressRequired(shop.value) || isRechargeRequired()) {
      customer.street = form.street;
      customer.streetNumber = form.streetNumber;
      customer.postalCode = form.postalCode;
      customer.city = form.city;
      customer.additionalAddress = form.additionalAddress;
      customer.phoneNumber = form.phoneNumber;
    }

    if (isRechargeRequired()) {
      customer.campaign = form.campaign;
    }

    if (redeemCampaign.value) {
      customer.campaign = redeemCampaign.value;
    }

    if (canRequestDonationReceipt(shop.value)) {
      customer.donationReceipt = form.donationReceipt;
    }

    const promotion = {} as Record<string, string | object>;

    if (form.orderPrepaidVouchersConfirmation) {
      promotion.name = 'prepaid-voucher-bundle';
      promotion.options = {};
    }

    redeem
      .redeemInShop(voucherToken.value, form.redeemValue, shop.value.id, customer, promotion)
      // eslint-disable-next-line consistent-return
      .then((response: AxiosResponse) => {
        if (response.data.success) {
          return callback(response.data);
        }
      })
      .catch((e: AxiosError) => {
        errors.value = e.response?.data.errors;
        submitted.value = false;
      });
  }
}

function next(data: unknown) {
  setFormData(data);

  if (wizardComponents.value[step.value].name === 'redeemWarnings' && !form.paymentRequired) {
    step.value = wizardComponents.value.findIndex((c) => c.name === 'redeemForm');
  } else if (step.value === wizardComponents.value.findIndex((c) => c.name === 'redeemForm')) {
    if (form.useRecharge) {
      step.value = wizardComponents.value.findIndex((c) => c.name === 'rechargeForm');
      return;
    }
    submitForm((responseData: Record<string, string>) => handleSubmitForm(responseData));
  } else if (step.value === wizardComponents.value.findIndex((c) => c.name === 'rechargeForm')) {
    submitForm((responseData: Record<string, string>) => handleSubmitForm(responseData));
  } else if (step.value === wizardComponents.value.findIndex((c) => c.name === 'shopInfo')) {
    api
      .get(
        `/shops/${props.id}/check-stock/${form.redeemValue}`,
        {
          headers: {
            'x-jwt-token': `${voucherToken.value}`,
          },
        },
      )
      .then((response: any) => {
        if (response.data.isAvailable) {
          step.value = wizardComponents.value.findIndex((c) => c.name === 'shopPromotion');
        } else {
          const availabilityWarningComponentIndex = wizardComponents.value.findIndex((c) => c.name === 'availabilityWarning');
          wizardComponents.value[availabilityWarningComponentIndex].props = {
            alternativeShops: response?.data?.shops,
          };
          step.value = availabilityWarningComponentIndex;
        }
      });
  } else {
    step.value += 1;
  }
}

onMounted(() => {
  voucherSerialNumber.value = String(voucher.value.serialNumber);
  store.dispatch('fetchShopDetails', props.id);

  const subComponentHash = route.hash.replace('#', '');
  if (subComponentHash) {
    switchComponent(subComponentHash);
  }
  clearFormData();
});

return { __sfc: true,form, route, router, props, step, wizardComponents, submitted, errors, shop, country, voucherToken, redeemCampaign, voucher, voucherSerialNumber, setFormData, clearFormData, switchComponent, prev, handleSubmitForm, submitForm, next, LoadingIndicator }
}

})